// TwoFactorInput.tsx - Componente para ingreso de código 2FA
import { useState, useRef, useEffect } from 'react';

interface TwoFactorInputProps {
  isOpen: boolean;
  onClose: () => void;
  onVerify: (code: string) => void;
  onUseBackupCode: (code: string) => void;
  isLoading: boolean;
  error: string;
  userEmail: string;
  userName: string;
}

const TwoFactorInput: React.FC<TwoFactorInputProps> = ({
  isOpen,
  onClose,
  onVerify,
  onUseBackupCode,
  isLoading,
  error,
  userEmail,
  userName
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [showBackupInput, setShowBackupInput] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Focus en el primer input cuando se abre
  useEffect(() => {
    if (isOpen && !showBackupInput) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen, showBackupInput]);

  // Limpiar cuando se cierra
  useEffect(() => {
    if (!isOpen) {
      setCode(['', '', '', '', '', '']);
      setBackupCode('');
      setShowBackupInput(false);
    }
  }, [isOpen]);

  const setInputRef = (index: number) => (el: HTMLInputElement | null) => {
    inputRefs.current[index] = el;
  };

  const handleChange = (index: number, value: string) => {
    // Solo permitir números
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus al siguiente input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit cuando se completan los 6 dígitos
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        onVerify(fullCode);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Retroceder con backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Enter para submit
    if (e.key === 'Enter') {
      const fullCode = code.join('');
      if (fullCode.length === 6) {
        onVerify(fullCode);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      onVerify(pastedData);
    }
  };

  const handleBackupSubmit = () => {
    if (backupCode.trim()) {
      onUseBackupCode(backupCode.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 transform animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-green-500 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Verificación en dos pasos
          </h2>
          <p className="text-gray-600">
            Hola <span className="font-semibold">{userName}</span>
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {userEmail}
          </p>
        </div>

        {!showBackupInput ? (
          <>
            {/* Instrucciones */}
            <p className="text-center text-gray-600 mb-6">
              Ingresa el código de 6 dígitos de tu app authenticator
            </p>

            {/* Inputs de código */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={setInputRef(index)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={isLoading}
                  className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl 
                    focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none
                    transition-all duration-200
                    ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}
                    ${isLoading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                  `}
                />
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            {/* Botón verificar */}
            <button
              onClick={() => onVerify(code.join(''))}
              disabled={code.join('').length !== 6 || isLoading}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300
                ${code.join('').length === 6 && !isLoading
                  ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white hover:shadow-lg hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verificando...
                </span>
              ) : (
                'Verificar'
              )}
            </button>

            {/* Link a código de respaldo */}
            <button
              onClick={() => setShowBackupInput(true)}
              className="w-full mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              ¿No tienes acceso a tu app? Usa un código de respaldo
            </button>
          </>
        ) : (
          <>
            {/* Input de código de respaldo */}
            <p className="text-center text-gray-600 mb-6">
              Ingresa uno de tus códigos de respaldo
            </p>

            <input
              type="text"
              value={backupCode}
              onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              disabled={isLoading}
              className={`w-full p-4 text-center text-xl font-mono border-2 rounded-xl mb-4
                focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none
                ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}
              `}
            />

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm text-center">{error}</p>
              </div>
            )}

            <button
              onClick={handleBackupSubmit}
              disabled={!backupCode.trim() || isLoading}
              className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300
                ${backupCode.trim() && !isLoading
                  ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white hover:shadow-lg hover:scale-105'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {isLoading ? 'Verificando...' : 'Usar código de respaldo'}
            </button>

            <button
              onClick={() => {
                setShowBackupInput(false);
                setBackupCode('');
              }}
              className="w-full mt-4 text-gray-600 hover:text-gray-800 text-sm font-medium"
            >
              ← Volver a código de app
            </button>
          </>
        )}

        {/* Botón cancelar */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="w-full mt-4 py-3 text-gray-500 hover:text-gray-700 text-sm"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
};

export default TwoFactorInput;